import { CommonModule } from '@angular/common';
import {
  Component,
  ViewChild,
  Output,
  EventEmitter,
  Input,
  OnInit,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  QaiButtonComponent,
  QaiDialogComponent,
  QaiFieldComponent,
  QaiMultiSelectComponent,
  QaiLinkButtonComponent,
  QaiSelectedSimulationsListComponent,
  SimulationItem,
  DraggableItem,
  QaiMultiSelectOption,
  ClearButtonConfig,
  QaiSimulationSettingLabels,
  QaiSelectedSimulationsListLabels,
  QaiSimulationSettingOption,
} from '@qai/ui';
import { ISimulation } from 'src/app/interfaces/self-serve/responses/isimulation';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';
import { AssignmentsService } from 'src/app/services/self-serve/assignments.service';
import { forkJoin, take } from 'rxjs';
import { ISimulationByModuleAssignment } from 'src/app/interfaces/self-serve/assignment-manager/responses/isimulation-by-module-assignment';
import { AssignmentConstants } from 'src/app/utils/constants/self-serve/assignment.constants';
import { AssignmentTypeEnum } from 'src/app/utils/enums/self-serve/assignment-type.enum';
import { IModuleWithCalls } from 'src/app/interfaces/self-serve/responses/imodule-with-calls';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { LocaleService } from 'src/app/services/locale.service';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { AppConstants } from 'src/app/app-constants';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { ComplianceScoreImpactEnum } from 'src/app/utils/enums/self-serve/compliance-score-impact.enum';
import { IAddSimulationsToAssignment } from 'src/app/interfaces/self-serve/assignment-manager/requests/isimulation-in-assignment';
import {
  DEFAULT_COMPLIANCE_SCORE_IMPACT,
  buildComplianceScoreImpactLabels,
  buildComplianceScoreImpactOptions,
} from 'src/app/utils/constants/self-serve/compliance-score-impact.constants';

const UNLIMITED_ATTEMPTS_VALUE = 'unlimited';

@Component({
  selector: 'add-simulation-to-assignment-modal',
  standalone: true,
  templateUrl: './add-simulation-to-assignment.component.html',
  styleUrls: ['./add-simulation-to-assignment.component.scss'],
  imports: [
    FormsModule,
    CommonModule,
    QaiDialogComponent,
    QaiButtonComponent,
    QaiMultiSelectComponent,
    QaiLinkButtonComponent,
    QaiSelectedSimulationsListComponent,
    QaiFieldComponent,
    TranslocoModule,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class AddSimulationModalComponent implements OnInit {
  @ViewChild('dialog') qaiDialogRef: QaiDialogComponent | undefined;
  @ViewChild(QaiMultiSelectComponent) multiSelectRef: QaiMultiSelectComponent | undefined;

  @Input() open: boolean = false;
  @Input() moduleAssignmentUuid: string = '';
  @Input() moduleUuid: string = '';
  @Input() modalHeading: string = 'Add simulation to assignment';
  @Input() isCreateAssignmentFlow: boolean = false;
  @Output() simulationAdded = new EventEmitter<string>();
  @Output() simulationAssigned = new EventEmitter<void>();
  @Output() backAndCloseModal = new EventEmitter<void>();
  @Output() closeSimulationModal = new EventEmitter<void>();

  readonly clearButtonConfig: ClearButtonConfig = { enable: true };

  simulationOptions: QaiMultiSelectOption[] = [];
  selectedSimulationIds: string[] = [];
  selectedSimulations: SimulationItem[] = [];
  fieldLabel: string = '';
  attemptsOptions: { value: string; label: string }[] = [
    { value: UNLIMITED_ATTEMPTS_VALUE, label: 'Unlimited' },
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4' },
    { value: '5', label: '5' },
    { value: '6', label: '6' },
    { value: '7', label: '7' },
    { value: '8', label: '8' },
    { value: '9', label: '9' },
    { value: '10', label: '10' },
  ];

  networkRequestInProgress: boolean = false;
  assignedSimulationIds: string[] = [];
  maxCallOrder: number = 0;
  showAttemptsAllowed: boolean = true;
  complianceScoreImpactEnabled: boolean = false;
  complianceScoreOptions: QaiSimulationSettingOption[] = [];
  complianceScoreLabels: QaiSimulationSettingLabels | undefined;
  listLabels: QaiSelectedSimulationsListLabels | undefined;
  private callsWithCompliance = new Set<string>();

  private readonly featureFlagsService = inject(FeatureFlagsService);
  private readonly amplitudeService = inject(AmplitudeService);

  constructor(
    private authorService: AuthorService,
    private assignmentsService: AssignmentsService,
    private translocoService: TranslocoService,
    private localeService: LocaleService
  ) {}

  async ngOnInit(): Promise<void> {
    this.translocoService.selectTranslation('simulationBuilder').pipe(take(1)).subscribe(() => {
      const translate = (key: string) => this.translocoService.translate(key);
      this.attemptsOptions[0] = {
        value: UNLIMITED_ATTEMPTS_VALUE,
        label: translate('simulationBuilder.ATTEMPTS_UNLIMITED'),
      };
      this.complianceScoreOptions = buildComplianceScoreImpactOptions(translate);
      this.complianceScoreLabels = buildComplianceScoreImpactLabels(translate);
      this.listLabels = {
        simulationName: translate('simulationBuilder.SIMULATION_NAME_LABEL'),
        numberOfAttempts: translate('simulationBuilder.TESTING_COL_NUMBER_OF_ATTEMPTS'),
        sortedAscending: translate('simulationBuilder.SIMULATIONS_LIST_SORTED_ASCENDING'),
        sortedDescending: translate('simulationBuilder.SIMULATIONS_LIST_SORTED_DESCENDING'),
      };
      this.updateFieldLabel();
    });
    this.complianceScoreImpactEnabled = await this.featureFlagsService.isFlagEnabled(
      AppConstants.COMPLIANCE_SCORE_IMPACT
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']) {
      const isOpenNow = changes['open']?.currentValue;

      if (isOpenNow) {
        this.selectedSimulationIds = [];
        this.selectedSimulations = [];
        this.simulationOptions = [];
        this.fetchSimulationsData();
        this.openModal();
      } else {
        if (this.qaiDialogRef) {
          this.qaiDialogRef.close();
        }
      }
    }
  }

  handleSuccessClick() {
    this.simulationAdded.emit();
  }

  handleBackAndCloseClick() {
    this.backAndCloseModal.emit();
  }

  handleCloseModal() {
    this.simulationOptions = [];
    this.closeSimulationModal.emit();
  }

  openModal() {
    this.selectedSimulations = [];
    this.selectedSimulationIds = [];
    this.updateFieldLabel();

    if (this.multiSelectRef) {
      this.multiSelectRef.clearSelections();
    }

    if (this.qaiDialogRef) {
      this.qaiDialogRef.showModal();
    }
  }

  closeModal() {
    if (this.qaiDialogRef) {
      this.qaiDialogRef.close();
    }
  }

  fetchSimulationsData(): void {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    this.networkRequestInProgress = true;

    forkJoin({
      allPublished: this.authorService.findPublishedCallsByAccountUuid(
        accountUuid
      ),
      assigned: this.assignmentsService.getSimulationsByModuleAssignmentUuid(
        this.moduleAssignmentUuid
      ),
    }).subscribe(
      (result: any) => {
        const allPublished: IModuleWithCalls[] = result.allPublished;
        const assigned: ISimulationByModuleAssignment = result.assigned;
        this.showAttemptsAllowed =
          assigned.moduleAssignmentType !==
          AssignmentTypeEnum.PERSONALIZED_PRACTICE;

        const assignedList =
          assigned && Array.isArray(assigned.simulations)
            ? assigned.simulations
            : [];
        const assignedIds = assignedList.map(
          (sim: any) => sim.callUuid || sim.id
        );
        this.simulationOptions = this.buildFlatOptions(allPublished, assignedIds);
        this.callsWithCompliance = new Set(
          allPublished
            .flatMap((module) => module.calls)
            .filter((call) => call.hasCompliance)
            .map((call) => call.callUuid)
        );

        this.maxCallOrder = assignedList.length
          ? Math.max(...assignedList.map((sim: any) => sim.callOrder || 0))
          : 0;
        this.networkRequestInProgress = false;
      },
      () => {
        this.simulationOptions = [];
        this.networkRequestInProgress = false;
      }
    );
  }

  private buildFlatOptions(
    modules: IModuleWithCalls[],
    assignedIds: string[]
  ): QaiMultiSelectOption[] {
    const seen = new Set<string>();
    return modules
      .flatMap((module) =>
        module.calls.map((call) => {
          // A Date (not the ISO string) so formatDate uses the user's local calendar day.
          const formattedDate = call.createdAt
            ? this.localeService.formatDate(new Date(call.createdAt))
            : '';
          return {
            value: call.callUuid,
            label: call.callName,
            description: `Created ${formattedDate}`,
            disabled: assignedIds.includes(call.callUuid),
          };
        })
      )
      .filter((option) => {
        if (seen.has(option.value)) return false;
        seen.add(option.value);
        return true;
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  onRemove(id: string): void {
    this.selectedSimulations = this.selectedSimulations.filter(
      (sim) => sim.id !== id
    );
    this.selectedSimulationIds = [
      ...this.selectedSimulations.map((sim) => sim.id),
    ];
    this.updateFieldLabel();
  }

  onSelectionSave(data: QaiMultiSelectOption[]): void {
    const selectedIds = data.map((option) => option.value);

    const existingIds = this.selectedSimulations.map((sim) => sim.id);
    const newIds = selectedIds.filter((id) => !existingIds.includes(id));

    const existingSimulations = this.selectedSimulations.filter((sim) =>
      selectedIds.includes(sim.id)
    );

    const newSimulations = newIds.map((id) => {
      const option = data.find((o) => o.value === id)!;
      const showSetting =
        this.complianceScoreImpactEnabled && this.callsWithCompliance.has(id);
      return {
        id,
        name: option.label,
        attempts: UNLIMITED_ATTEMPTS_VALUE,
        attemptsOptions: this.attemptsOptions,
        showSetting,
        setting: showSetting ? DEFAULT_COMPLIANCE_SCORE_IMPACT : null,
      };
    });

    this.selectedSimulations = [...existingSimulations, ...newSimulations];
    this.selectedSimulationIds = [...selectedIds];
    this.updateFieldLabel();
  }

  onRemoveAll(): void {
    this.selectedSimulations = [];
    this.selectedSimulationIds = [];
    if (this.multiSelectRef) {
      this.multiSelectRef.clearSelections();
    }
    this.updateFieldLabel();
  }

  onSimulationListSelectionChange(reorderedSimulations: DraggableItem[]): void {
    this.selectedSimulations = reorderedSimulations.map((item) => {
      const simulation = this.selectedSimulations.find(
        (sim) => sim.id === item['id']
      );
      return simulation!;
    });
    this.selectedSimulationIds = [
      ...this.selectedSimulations.map((sim) => sim.id),
    ];
  }

  onAttemptsChange(id: string, attempts: string): void {
    this.selectedSimulations = this.selectedSimulations.map((sim) => {
      if (sim.id === id) {
        return { ...sim, attempts };
      }
      return sim;
    });
  }

  onComplianceScoreChange(id: string, complianceScore: string): void {
    this.selectedSimulations = this.selectedSimulations.map((sim) =>
      sim.id === id ? { ...sim, setting: complianceScore } : sim
    );
  }

  private updateFieldLabel(): void {
    const baseLabel = this.translocoService.translate('simulationBuilder.SEARCH_FOR_SIMULATION_LABEL');
    this.fieldLabel = this.selectedSimulations.length > 0
      ? `${baseLabel} (${this.selectedSimulations.length} selected)`
      : baseLabel;
  }

  assignSimulationsToAssignment(): void {
    if (!this.moduleAssignmentUuid || this.selectedSimulations.length === 0) {
      return;
    }
    this.networkRequestInProgress = true;
    const payload: IAddSimulationsToAssignment = {
      moduleAssignmentUuid: this.moduleAssignmentUuid,
      simulations: this.selectedSimulations.map((sim, index) => ({
        callUuid: sim.id,
        numberOfAttemptsAllowed:
          sim.attempts === UNLIMITED_ATTEMPTS_VALUE
            ? null
            : Number(sim.attempts),
        callOrder: this.maxCallOrder + index + 1,
        ...(sim.showSetting && { complianceScoreImpact: sim.setting as ComplianceScoreImpactEnum }),
      })),
    };

    this.assignmentsService.addSimulationsToAssignment(payload).subscribe({
      next: () => {
        this.selectedSimulations
          .filter((sim) => sim.showSetting)
          .forEach((sim) =>
            this.amplitudeService.trackEvent(AmplitudeTrackingEvents.COMPLIANCE_SCORE_IMPACT_SET, {
              assignment_uuid: this.moduleAssignmentUuid,
              call_uuid: sim.id,
              compliance_feedback: sim.setting,
            })
          );
        this.simulationAssigned.emit();
        this.networkRequestInProgress = false;
      },
      error: () => {
        this.networkRequestInProgress = false;
      },
    });
  }
}
